class DeliveryEstimator extends HTMLElement {
  constructor() {
    super()
    this.timerInterval = null
    this.cutoffHour = 13
    this.cutoffMinute = 30
    this.warehouseTimeZone = 'America/Toronto'
  }

  connectedCallback() {
    // 1. Load Data
    this.productPrice = parseInt(this.dataset.productPrice)
    this.threshold = parseInt(this.dataset.threshold)
    this.freeText = this.dataset.freeText
    this.fromPrice = this.dataset.fromPrice

    try {
      const configData = JSON.parse(this.querySelector('script[type="application/json"]').textContent)
      this.configData = configData
      this.holidays = Array.isArray(configData.holidays) ? configData.holidays : []
      this.postalZones = configData.postal || configData.postal_zones || {}
      this.pickupInfo = configData.pickup || {}
    } catch (e) {
      console.error('Delivery Widget: Invalid JSON config', e)
    }

    // 2. Select Elements
    this.elements = {
      tabs: this.querySelectorAll('[role="tab"]'),
      panels: this.querySelectorAll('[role="tabpanel"]'),
      zipInput: this.querySelector('.zip-input'),
      checkBtn: this.querySelector('.check-btn'),
      errorMsg: this.querySelector('.error-msg'),
      inputState: this.querySelector('.delivery-estimator__state-input'),
      resultState: this.querySelector('.delivery-estimator__state-result'),
      pickupLocations: this.querySelector('[data-pickup-locations]'),
      priceDisplays: this.querySelectorAll('.price-display, .final-cost'),
      displayZip: this.querySelector('.display-zip'),
      shipDate: this.querySelector('[data-ship-date]'),
      arrivalDate: this.querySelector('.arrival-date'),
      pickupTime: this.querySelector('[data-pickup-time]'),
      timer: this.querySelector('.timer-display'),
      modifyLink: this.querySelector('.modify-link'),
    }

    // 3. Initialize
    this.initPricing()
    this.initPickupInfo()
    this.setupEventListeners()
    this.checkLocalStorage()
  }

  disconnectedCallback() {
    if (this.timerInterval) clearInterval(this.timerInterval)
  }

  initPricing() {
    const isFree = this.productPrice >= this.threshold
    const displayText = isFree ? this.freeText : this.fromPrice

    this.elements.priceDisplays.forEach((el) => {
      el.textContent = displayText
      if (isFree) el.classList.add('free-shipping')
    })

    if (isFree) {
      const deliveryBadge = this.querySelector('#tab-delivery .delivery-estimator__badge')
      if (deliveryBadge) {
        deliveryBadge.textContent = this.freeText
        deliveryBadge.classList.add('free-shipping')
      }
    }
  }

  initPickupInfo() {
    this.initPickupLocations()

    if (!this.elements.pickupTime) return

    const pickupTime = this.getLocalizedValue(this.pickupInfo.time)

    if (pickupTime) {
      this.elements.pickupTime.textContent = pickupTime
    }
  }

  initPickupLocations() {
    if (!this.elements.pickupLocations) return

    const locations = Array.isArray(this.pickupInfo.locations) ? this.pickupInfo.locations : []

    if (!locations.length) return

    const iconMarkup = this.elements.pickupLocations.querySelector('.icon-pin')?.outerHTML || ''

    this.elements.pickupLocations.innerHTML = locations
      .map((location) => {
        const addressParts = [
          this.getLocalizedValue(location.address1),
          this.getLocalizedValue(location.address2),
          this.getLocalizedValue(location.city),
          this.getLocalizedValue(location.province),
        ].filter(Boolean)

        if (!addressParts.length) return ''

        return `
          <div class="store-row">
            <div class="store-info">
              ${iconMarkup}
              <span>${this.escapeHtml(addressParts.join(', '))}</span>
            </div>
          </div>
        `
      })
      .join('')
  }

  setupEventListeners() {
    // Tabs
    this.elements.tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => this.switchTab(e.currentTarget))
      // Accessibility: Keyboard nav for tabs
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.switchTab(e.currentTarget)
        }
      })
    })

    // Check Button
    this.elements.checkBtn.addEventListener('click', () => this.handleCalculate())

    // Enter Key on Input
    this.elements.zipInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        this.handleCalculate()
      }
    })

    // Remove Error on typing
    this.elements.zipInput.addEventListener('input', () => {
      this.elements.errorMsg.classList.add('hidden')
      this.elements.zipInput.setAttribute('aria-invalid', 'false')
    })

    // Modify Link
    if (this.elements.modifyLink) {
      this.elements.modifyLink.addEventListener('click', (e) => {
        e.preventDefault()
        this.toggleView('input')
        setTimeout(() => this.elements.zipInput.focus(), 100)
      })
    }
  }

  switchTab(selectedTab) {
    const targetId = selectedTab.getAttribute('aria-controls')

    // Deactivate all
    this.elements.tabs.forEach((t) => {
      t.classList.remove('active')
      t.setAttribute('aria-selected', 'false')
    })
    this.elements.panels.forEach((p) => {
      p.classList.remove('active')
      p.hidden = true
    })

    // Activate selected
    selectedTab.classList.add('active')
    selectedTab.setAttribute('aria-selected', 'true')

    const targetPanel = this.querySelector(`#${targetId}`)
    if (targetPanel) {
      targetPanel.classList.add('active')
      targetPanel.hidden = false
    }
  }

  handleCalculate() {
    const zip = this.elements.zipInput.value.trim()
    if (!zip || zip.length < 3) return

    const cleanZip = zip.toUpperCase().replace(/\s/g, '')
    const firstChar = cleanZip.charAt(0)
    const region = this.postalZones[firstChar]

    // Validation
    if (!region) {
      this.elements.errorMsg.classList.remove('hidden')
      this.elements.zipInput.setAttribute('aria-invalid', 'true')
      return
    }

    // Success Logic
    this.calculateDelivery(region, cleanZip)

    // Persist user-entered postal for next visit
    localStorage.setItem('shopify_delivery_zip', zip)
  }

  calculateDelivery(region, cleanZip) {
    const shippingRegion = region.shipping || region
    const shippingDays = parseInt(shippingRegion.days, 10)

    if (Number.isNaN(shippingDays)) {
      this.elements.errorMsg.classList.remove('hidden')
      this.elements.zipInput.setAttribute('aria-invalid', 'true')
      return
    }

    const warehouseNow = this.getWarehouseNow()
    const holidayList = this.holidays
    const shippingDate = this.getShippingDate(warehouseNow, holidayList)
    const deliveryDate = this.getDeliveryDateFromShippingDate(shippingDate, shippingDays, holidayList)

    // Formatting
    const formattedZip = cleanZip.length >= 6 ? `${cleanZip.substring(0, 3)} ${cleanZip.substring(3)}` : cleanZip

    // Update DOM
    this.elements.displayZip.textContent = formattedZip
    if (this.elements.shipDate) {
      this.elements.shipDate.textContent = this.getRelativeOrFormattedDate(shippingDate, warehouseNow)
    }
    this.elements.arrivalDate.textContent = this.formatTimelineDate(deliveryDate)

    this.toggleView('result')
    this.startTimer()
  }

  toggleView(viewName) {
    if (viewName === 'result') {
      this.elements.inputState.classList.add('hidden')
      this.elements.resultState.classList.remove('hidden')
    } else {
      this.elements.resultState.classList.add('hidden')
      this.elements.inputState.classList.remove('hidden')
    }
  }

  checkLocalStorage() {
    const savedZip = localStorage.getItem('shopify_delivery_zip')

    if (savedZip) {
      this.elements.zipInput.value = savedZip
      this.handleCalculate()
    }
  }

  getLocalizedValue(value) {
    if (!value) return null
    if (typeof value === 'string') return value

    const documentLang = (document.documentElement.lang || 'en').split('-')[0]

    return value[documentLang] || value.en || value.fr || null
  }

  getDocumentLanguage() {
    return (document.documentElement.lang || 'en').split('-')[0]
  }

  formatDateKey(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  isHoliday(date, holidays = []) {
    return holidays.includes(this.formatDateKey(date))
  }

  isBusinessDay(date, holidays = []) {
    const day = date.getDay()
    return day !== 0 && day !== 6 && !this.isHoliday(date, holidays)
  }

  getNextBusinessDay(date, holidays = []) {
    const nextDate = new Date(date)

    do {
      nextDate.setDate(nextDate.getDate() + 1)
    } while (!this.isBusinessDay(nextDate, holidays))

    return nextDate
  }

  getShippingDate(baseDate, holidays = []) {
    const currentDate = new Date(baseDate)
    const cutoffDate = new Date(currentDate)
    cutoffDate.setHours(this.cutoffHour, this.cutoffMinute, 0, 0)

    if (!this.isBusinessDay(currentDate, holidays)) {
      return this.getNextBusinessDay(currentDate, holidays)
    }

    if (currentDate >= cutoffDate) {
      return this.getNextBusinessDay(currentDate, holidays)
    }

    return currentDate
  }

  getDeliveryDateFromShippingDate(shippingDate, shippingDays, holidays = []) {
    const deliveryDate = new Date(shippingDate)
    let daysRemaining = shippingDays

    while (daysRemaining > 0) {
      deliveryDate.setDate(deliveryDate.getDate() + 1)

      if (this.isBusinessDay(deliveryDate, holidays)) {
        daysRemaining -= 1
      }
    }

    return deliveryDate
  }

  isSameCalendarDay(firstDate, secondDate) {
    return this.formatDateKey(firstDate) === this.formatDateKey(secondDate)
  }

  formatTimelineDate(date) {
    const locale = this.getDocumentLanguage() === 'fr' ? 'fr-CA' : 'en-CA'

    return date.toLocaleDateString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  getRelativeOrFormattedDate(targetDate, referenceDate) {
    if (this.isSameCalendarDay(targetDate, referenceDate)) {
      return this.getDocumentLanguage() === 'fr' ? "aujourd'hui" : 'Today'
    }

    return this.formatTimelineDate(targetDate)
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')
  }

  // --- Timezone Helpers ---

  getWarehouseNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: this.warehouseTimeZone }))
  }

  startTimer() {
    if (this.timerInterval) clearInterval(this.timerInterval)

    const updateTimer = () => {
      const nowEst = this.getWarehouseNow()
      const cutoff = this.getCutoffDeadline(nowEst, this.holidays)
      const diff = Math.max(cutoff - nowEst, 0)
      const h = Math.floor(diff / 3600000)
      const m = Math.floor((diff % 3600000) / 60000)

      this.elements.timer.textContent = `${h}h ${m}m`
    }

    updateTimer()
    this.timerInterval = setInterval(updateTimer, 60000)
  }

  getCutoffDeadline(baseDate, holidays = []) {
    const now = new Date(baseDate)
    const cutoff = new Date(now)
    cutoff.setHours(this.cutoffHour, this.cutoffMinute, 0, 0)

    if (this.isBusinessDay(now, holidays) && now < cutoff) {
      return cutoff
    }

    const nextBusinessDay = this.getNextBusinessDay(now, holidays)
    nextBusinessDay.setHours(this.cutoffHour, this.cutoffMinute, 0, 0)

    return nextBusinessDay
  }
}

customElements.define('delivery-estimator', DeliveryEstimator)
